import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import StyleIcon from '@mui/icons-material/Style';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedIcon from '@mui/icons-material/Verified';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { PREMIUM_PAYMENT_LINK } from '../utils/payment';
import { trackPremiumView, trackCheckoutStarted } from '../utils/analytics';
import { getFreeStoriesUsedSync } from '../utils/freeStoryTracker';
import { readStats } from '../utils/userStats';

const FAQS = [
  {
    q: 'What do I get with premium?',
    a: 'Full access to all HSK stories across every level, native audio with karaoke-style highlighting, progress tracking across devices, and all future stories added to the library.',
  },
  {
    q: "What's included in the free plan?",
    a: "The first episode of every series and a selection of standalone stories are always free. No account needed to start reading.",
  },
  {
    q: 'How much does premium cost?',
    a: 'Premium is $4.99/month, billed monthly. You can cancel any time and keep access until the end of your billing period.',
  },
  {
    q: 'Is there a money-back guarantee?',
    a: (
      <>
        Yes: within 14 days of purchase, no questions asked. Email dumplingo.official@gmail.com with your purchase email and we will refund you in full. See our{' '}
        <Typography
          component="a"
          href="/assets/Legal/refund-policy.html"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: '#FF6123', textDecoration: 'underline', fontSize: 'inherit', fontFamily: 'inherit', fontWeight: 600 }}
        >
          Refund Policy
        </Typography>
        {' '}for details.
      </>
    ),
  },
  {
    q: 'How is payment handled?',
    a: 'Payments are processed securely by Stripe. We never see or store your card details.',
  },
];

function FaqSection() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <Box sx={{ width: '100%', borderTop: '1px solid #ede6e0', pt: 2.5 }}>
      <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: 15, color: '#2c2017', mb: 1.5 }}>
        FAQ
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {FAQS.map((faq, i) => (
          <Box
            key={i}
            sx={{
              borderRadius: 2,
              border: '1.5px solid #ede6e0',
              overflow: 'hidden',
              background: openIdx === i ? '#faf7f5' : '#fff',
            }}
          >
            <ButtonBase
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
              disableRipple
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 2,
                py: 1.5,
                textAlign: 'left',
              }}
            >
              <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontWeight: 700, fontSize: 13.5, color: '#2c2017', flex: 1, pr: 1 }}>
                {faq.q}
              </Typography>
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  color: '#9e8e84',
                  flexShrink: 0,
                  transform: openIdx === i ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </ButtonBase>
            {openIdx === i && (
              <Box sx={{ px: 2, pb: 1.5 }}>
                <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontSize: 13, color: '#6b5a4f', lineHeight: 1.55 }}>
                  {faq.a}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const DEFAULT_USERS = 150;

// Feature list
const FEATURES = [
  { icon: <AutoStoriesIcon sx={{ fontSize: 20, color: '#FF6123' }} />, text: 'Graded stories across HSK 1-6, with new stories added regularly' },
  { icon: <VolumeUpIcon sx={{ fontSize: 20, color: '#FF6123' }} />, text: 'Native audio synced word-by-word for every story' },
  { icon: <StyleIcon sx={{ fontSize: 20, color: '#FF6123' }} />, text: 'Progress tracking, reading streak, and resume on any device' },
];

export default function PremiumPaywallModal({ open, onClose, user, onLogin, onUpgradeSuccess }) {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [userCount, setUserCount] = useState(DEFAULT_USERS);

  // Fetch live user count from Firestore when modal opens
  useEffect(() => {
    if (!open) return;
    trackPremiumView('paywall');
    const db = getFirestore();
    getDoc(doc(db, 'siteStats', 'global'))
      .then(snap => {
        if (snap.exists()) {
          const data = snap.data();
          if (typeof data.userCount === 'number') setUserCount(data.userCount);
        }
      })
      .catch(() => {}); // non-fatal
  }, [open]);

  const handleContinue = async () => {
    if (!user) {
      if (onLogin) { onClose(); onLogin('premium'); }
      else setError('Please sign in first');
      return;
    }
    setProcessing(true);
    setError('');
    try {
      const url = new URL(PREMIUM_PAYMENT_LINK);
      if (user?.uid) url.searchParams.set('client_reference_id', user.uid);
      if (user?.email) url.searchParams.set('prefilled_email', user.email);
      trackCheckoutStarted('monthly');
      localStorage.setItem('pendingPurchaseTrack', 'monthly');
      window.location.href = url.toString();
    } catch (err) {
      console.error('Payment error:', err);
      setError('An error occurred. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={processing ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxWidth: 400,
          background: '#ffffff',
          color: '#2c2017',
          overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        }
      }}
    >
      <DialogContent sx={{ p: 0, '&::-webkit-scrollbar': { display: 'none' } }}>
        {/* Close button */}
        {!processing && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#b0a09a',
              zIndex: 10,
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}

        <Box sx={{ px: 3, pt: 4, pb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

          {/* Dumplingo wordmark */}
          <Box component="img" src={`${process.env.PUBLIC_URL || ''}/assets/logo.png`} alt="Dumplingo" sx={{ height: 36, width: 'auto', display: 'block', mb: 2, filter: 'brightness(0) saturate(100%) invert(40%) sepia(90%) saturate(600%) hue-rotate(346deg) brightness(105%)' }} />

          {/* Heading */}
          <Typography
            sx={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontStyle: 'italic',
              fontWeight: 700,
              fontSize: 24,
              color: '#2c2017',
              textAlign: 'center',
              mb: 0.2,
              lineHeight: 1.2,
            }}
          >
            Thank you for supporting us
          </Typography>


          {/* Thanks image */}
          <Box component="img" src={`${process.env.PUBLIC_URL || ''}/assets/thanks.png`} alt="Thank you" sx={{ width: '100%', maxWidth: 320, display: 'block', mx: 'auto', mb: 0.5, objectFit: 'contain' }} />

          {/* Price Card */}
          <Box
            sx={{
              width: '100%',
              borderRadius: 2,
              border: '2px solid #FF6123',
              p: 2,
              mb: 2,
              background: 'rgba(255,97,35,0.06)',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontSize: 12, color: '#FF6123', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
              Dumplingo Premium
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
              <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontSize: 36, fontWeight: 700, color: '#2c2017', lineHeight: 1 }}>
                $4.99
              </Typography>
              <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontSize: 15, fontWeight: 600, color: '#9e8e84' }}>
                /month
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontSize: 12, color: '#9e8e84', mt: 0.5 }}>
              Billed monthly - cancel any time
            </Typography>
          </Box>

          {/* Error */}
          {error && (
            <Box sx={{ bgcolor: '#fff0ef', border: '1px solid #ffb4a8', borderRadius: 1.5, p: 1.5, mb: 2, width: '100%' }}>
              <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontSize: 13, color: '#c0392b', textAlign: 'center' }}>
                {error}
              </Typography>
            </Box>
          )}

          {/* CTA Button */}
          <Button
            onClick={handleContinue}
            disabled={processing}
            fullWidth
            variant="contained"
            sx={{
              textTransform: 'none',
              fontFamily: '"Raleway", sans-serif',
              fontWeight: 700,
              fontSize: 17,
              py: 1.75,
              background: '#FF6123',
              color: 'white',
              borderRadius: 50,
              boxShadow: '0 4px 16px rgba(255,97,35,0.35)',
              '&:hover': { background: '#e55520', boxShadow: '0 6px 20px rgba(255,97,35,0.45)' },
              '&:disabled': { background: '#e0d5cf', color: '#b0a09a' },
              mb: 1.5,
            }}
          >
            {processing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'center' }}>
                <CircularProgress size={22} sx={{ color: 'white' }} />
                <span>Processing...</span>
              </Box>
            ) : (
              'Unlock all stories'
            )}
          </Button>

          {/* Stripe security note */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mb: 1 }}>
            <LockIcon sx={{ fontSize: 13, color: '#b0a09a' }} />
            <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontSize: 12, color: '#b0a09a' }}>
              Secure payment via Stripe
            </Typography>
          </Box>

          {/* Footer note */}
          <Typography
            sx={{
              fontFamily: '"Raleway", sans-serif',
              fontSize: 12,
              color: '#b0a09a',
              textAlign: 'center',
              mb: 2,
            }}
          >
            Monthly subscription · Trusted by {userCount}+ learners
          </Typography>

          {/* Terms / Privacy */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            {[
              ['Terms', '/assets/Legal/Termify-Terms-and-Conditions-QqPSWMCXKF.pdf'],
              ['Privacy', '/assets/Legal/Termify-Privacy-Policy-QqPSWMCXKF (1).pdf'],
            ].map(([label, href]) => (
              <Typography
                key={label}
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontFamily: '"Raleway", sans-serif',
                  fontSize: 12,
                  color: '#b0a09a',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  '&:hover': { color: '#6b5a4f' },
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>

          {/* Features */}
          <Box sx={{ width: '100%', mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {FEATURES.map((f, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    background: 'rgba(255,97,35,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {f.icon}
                </Box>
                <Typography
                  sx={{
                    fontFamily: '"Raleway", sans-serif',
                    fontSize: 14.5,
                    fontWeight: 600,
                    color: '#3b2d23',
                    lineHeight: 1.3,
                  }}
                >
                  {f.text}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Money-back guarantee badge */}
          <Box
            sx={{
              width: '100%',
              mb: 3,
              px: 2,
              py: 1.25,
              borderRadius: 2,
              background: 'rgba(46,125,50,0.07)',
              border: '1.5px solid rgba(46,125,50,0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
            }}
          >
            <VerifiedIcon sx={{ fontSize: 22, color: '#2e7d32', flexShrink: 0 }} />
            <Typography sx={{ fontFamily: '"Raleway", sans-serif', fontSize: 13, fontWeight: 700, color: '#2e7d32', lineHeight: 1.35 }}>
              Not happy? Email us within 14 days of purchase and we'll refund you in full.
            </Typography>
          </Box>

          {/* FAQ */}
          <FaqSection />

        </Box>
      </DialogContent>
    </Dialog>
  );
}
